import { ROYALTY_CURRENCY_CODES } from '@/lib/currencies'

export const DEFAULT_FIXED_ROYALTY_BASE_AMOUNT = '12668.48'
export const DEFAULT_LISTOK_CRM_BASE_AMOUNT = '10384'

// Модель продаж студии задаёт условие договора (решение 19.10.2026): обычное условие —
// абонементы, подписочное — «Модель 1 — подписки» или «Модель 2 — гибрид» (sales_model).
// Для роялти гибрид = подписки; из модели условия сервер выводит модель студии.
export type TermSalesModel = 'SUBSCRIPTION' | 'MIXED'
export type TermModel = 'REGULAR' | TermSalesModel

export const TERM_MODEL_OPTIONS: ReadonlyArray<readonly [TermModel, string]> = [
  ['REGULAR', 'Обычные (абонементы)'],
  ['SUBSCRIPTION', 'Модель 1 — подписки'],
  ['MIXED', 'Модель 2 — гибрид'],
]

export const TERM_MODEL_SHORT_LABELS: Record<TermModel, string> = {
  REGULAR: 'Абонементы',
  SUBSCRIPTION: 'Подписки',
  MIXED: 'Гибрид',
}

export function termModelOf(terms: { contract_type: string | null; sales_model?: string | null }): TermModel {
  if (terms.contract_type !== 'SUBSCRIPTION') return 'REGULAR'
  return terms.sales_model === 'MIXED' ? 'MIXED' : 'SUBSCRIPTION'
}

export function termModelLabel(terms: { contract_type: string | null; sales_model?: string | null }): string {
  const model = termModelOf(terms)
  return model === 'MIXED' ? 'гибрид' : model === 'SUBSCRIPTION' ? 'подписки' : 'абонементы'
}

/** Значение sales_model для записи: у обычного условия NULL. */
export function termSalesModelPayload(form: Pick<ContractTermsFormData, 'contract_type' | 'sales_model'>): TermSalesModel | null {
  return form.contract_type === 'SUBSCRIPTION' ? form.sales_model : null
}

export type ContractTermsFormData = {
  contract_type: 'REGULAR' | 'SUBSCRIPTION'
  sales_model: TermSalesModel
  effective_from: string
  effective_to: string
  royalty_percent: string
  agency_percent: string
  vat_percent: string
  listok_crm_base_amount: string
  is_listok_crm_carrier: boolean
  is_fixed_royalty_carrier: boolean
  min_royalty_amount: string
  currency_code: string
  min_royalty_currency: string
  source_document_note: string
  notes: string
}

export function createEmptyContractTermsForm(): ContractTermsFormData {
  return {
    contract_type: 'REGULAR',
    sales_model: 'SUBSCRIPTION',
    effective_from: '',
    effective_to: '',
    royalty_percent: '',
    agency_percent: '1',
    vat_percent: '22',
    listok_crm_base_amount: DEFAULT_FIXED_ROYALTY_BASE_AMOUNT,
    is_listok_crm_carrier: false,
    is_fixed_royalty_carrier: false,
    min_royalty_amount: '10000',
    currency_code: 'RUB',
    // '' = минималка в валюте обязательства (currency_code).
    min_royalty_currency: '',
    source_document_note: '',
    notes: '',
  }
}

const inputCls = 'w-full text-xs border border-border rounded px-2 py-1.5 bg-background disabled:opacity-60'

export function ContractTermsFormFields({
  form,
  onChange,
  showContractType = true,
  modelLocked = false,
  showEffectiveTo = true,
  effectiveFromLabel = 'Действует с',
  effectiveToLabel = 'Действует по',
}: {
  form: ContractTermsFormData
  onChange: <K extends keyof ContractTermsFormData>(field: K, value: ContractTermsFormData[K]) => void
  showContractType?: boolean
  /** Существующее условие: модель не меняется, переход — новым интервалом с датой. */
  modelLocked?: boolean
  showEffectiveTo?: boolean
  effectiveFromLabel?: string
  effectiveToLabel?: string
}) {
  return (
    <div className="space-y-2">
      {showContractType && (
        <div>
          <label className="text-[10px] text-muted-foreground">Модель продаж</label>
          <select
            value={termModelOf(form)}
            disabled={modelLocked}
            onChange={(e) => {
              const model = e.target.value as TermModel
              onChange('contract_type', model === 'REGULAR' ? 'REGULAR' : 'SUBSCRIPTION')
              if (model !== 'REGULAR') onChange('sales_model', model)
            }}
            className={inputCls}
          >
            {TERM_MODEL_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <p className="mt-0.5 text-[10px] leading-4 text-muted-foreground">
            {modelLocked
              ? 'Модель у существующего интервала не меняется: переход (абонементы → подписки, гибрид → подписки) оформляется новым интервалом с датой.'
              : 'Модель студии в метриках, рейтингах и планировании берётся из условия, действующего на дату. Для роялти гибрид считается как подписки.'}
          </p>
        </div>
      )}

      <div className={`grid gap-2 ${showEffectiveTo ? 'grid-cols-2' : 'sm:grid-cols-2'}`}>
        <div>
          <label className="text-[10px] text-muted-foreground">{effectiveFromLabel}</label>
          <input
            type="date"
            value={form.effective_from}
            onChange={(e) => onChange('effective_from', e.target.value)}
            className={inputCls}
          />
        </div>
        {showEffectiveTo && (
          <div>
            <label className="text-[10px] text-muted-foreground">{effectiveToLabel}</label>
            <input
              type="date"
              value={form.effective_to}
              onChange={(e) => onChange('effective_to', e.target.value)}
              className={inputCls}
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-muted-foreground">Ставка роялти, %</label>
          <input
            type="number"
            step="0.1"
            placeholder="% роялти"
            value={form.royalty_percent}
            onChange={(e) => onChange('royalty_percent', e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground">Валюта роялти</label>
          <select
            value={form.currency_code}
            onChange={(e) => {
              onChange('currency_code', e.target.value)
              onChange('min_royalty_currency', '')
            }}
            className={inputCls}
          >
            {ROYALTY_CURRENCY_CODES.map((currency) => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-muted-foreground">Минимальное роялти</label>
          <input
            type="number"
            placeholder="Минимум"
            value={form.min_royalty_amount}
            onChange={(e) => onChange('min_royalty_amount', e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground">Валюта минимума</label>
          <div className={`${inputCls} flex items-center text-muted-foreground`}>
            {form.currency_code}
          </div>
        </div>
      </div>

      <p className="text-[10px] leading-4 text-muted-foreground">
        Минимум всегда задан в валюте роялти.
      </p>

      {form.contract_type === 'SUBSCRIPTION' && (
        <div className="rounded-md bg-blue-50 p-2 space-y-2">
          <p className="text-[10px] font-medium text-blue-700">
            Параметры подписочного договора
          </p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] text-blue-700">Агентское, %</label>
              <input
                type="number"
                step="0.01"
                placeholder="Агентское %"
                value={form.agency_percent}
                onChange={(e) => onChange('agency_percent', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-[10px] text-blue-700">НДС, %</label>
              <input
                type="number"
                step="1"
                placeholder="НДС %"
                value={form.vat_percent}
                onChange={(e) => onChange('vat_percent', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-[10px] text-blue-700">Ручное агентское, ₽</label>
              <input
                type="number"
                step="1"
                placeholder="Сумма ручного агентского"
                value={form.listok_crm_base_amount}
                onChange={(e) => onChange('listok_crm_base_amount', e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          <p className="text-[10px] leading-4 text-blue-700/80">
            Ручное агентское всегда в рублях (₽), даже если роялти в иностранной
            валюте; сумма не конвертируется.
          </p>
          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.is_listok_crm_carrier}
              onChange={(e) => onChange('is_listok_crm_carrier', e.target.checked)}
            />
            <span>Начислять ручное агентское с этого договора</span>
          </label>
        </div>
      )}

      {form.contract_type === 'REGULAR' && (
        <div className="space-y-2">
          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.is_fixed_royalty_carrier}
              disabled={form.currency_code !== 'RUB' && !form.is_fixed_royalty_carrier}
              onChange={(e) => onChange('is_fixed_royalty_carrier', e.target.checked)}
            />
            <span>
              Начислять фиксированную часть роялти с этого договора
              <span className="block text-[10px]">
                {form.currency_code === 'RUB'
                  ? 'Ежемесячно, без НДС; сумму месяца можно изменить корректировкой при выставлении.'
                  : 'Только для договоров с роялти в рублях.'}
              </span>
            </span>
          </label>
          {form.is_fixed_royalty_carrier && (
            <div>
              <label className="text-[10px] text-muted-foreground">Фиксированная часть, ₽ в месяц (без НДС)</label>
              <input
                type="number"
                step="0.01"
                placeholder="Фиксированная часть"
                value={form.listok_crm_base_amount}
                onChange={(e) => onChange('listok_crm_base_amount', e.target.value)}
                className={inputCls}
              />
            </div>
          )}
        </div>
      )}

      <input
        type="text"
        placeholder="Документ-основание"
        value={form.source_document_note}
        onChange={(e) => onChange('source_document_note', e.target.value)}
        className={inputCls}
      />
      <input
        type="text"
        placeholder="Комментарий"
        value={form.notes}
        onChange={(e) => onChange('notes', e.target.value)}
        className={inputCls}
      />
    </div>
  )
}
